import { useState, useMemo } from 'react';
import GHLTagsInput from './GHLTagsInput';
import { PRESETS } from '../consts/urlPresets';

export default function UTMGenerator() {
  const [baseUrl, setBaseUrl] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('YouTube');
  const [wildcards, setWildcards] = useState<Record<string, string>>({});
  const [ghlTags, setGhlTags] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  const resolvedParams = useMemo(() => {
    const preset = PRESETS[selectedPreset];
    const wildcardParams = Object.entries(preset)
      .map(([key, value]) => {
        const replaced = value.replace(/{(.*?)}/g, (_, w) => wildcards[w] || '');
        return [key, replaced];
      })
      .filter(([, val]) => val !== '')
      .map(([key, val]) => `${key}=${encodeURIComponent(val)}`);

    if (ghlTags.length > 0) {
      wildcardParams.push(`ghl_tag=${ghlTags.map((t) => encodeURIComponent(t)).join(',')}`);
    }

    return wildcardParams.join('&');
  }, [selectedPreset, wildcards, ghlTags]);

  const generatedUrl = useMemo(() => {
    if (!baseUrl) return '';
    const sanitized = baseUrl.trim().replace(/^https?:\/\//, '');
    const fullBase = `https://${sanitized}`;
    if (!resolvedParams) return fullBase;
    const separator = fullBase.includes('?') ? '&' : '?';
    return `${fullBase}${separator}${resolvedParams}`;
  }, [baseUrl, resolvedParams]);

  const wildcardKeys = useMemo(() => {
    const values = Object.values(PRESETS[selectedPreset]);
    const matches = values
      .map((v) => v.match(/{(.*?)}/g) || [])
      .flat()
      .map((w) => w.replace(/[{}]/g, ''));
    return [...new Set(matches)];
  }, [selectedPreset]);

  const handleCopy = () => {
    if (!generatedUrl) return;
    navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <div>
        <label className="block text-sm font-medium mb-1">Base URL:</label>

        <div className="flex rounded border border-gray-300 overflow-hidden focus-within:ring-1 focus-within:ring-[#EE3333] focus-within:border-[#EE3333]">
          <label
            htmlFor="base-url"
            className="bg-gray-100 px-3 py-2 text-gray-600 text-sm select-none"
          >
            https://
          </label>
          <input
            id="base-url"
            type="text"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value.replace(/^https?:\/\//, '').trim())}
            placeholder="example.com/landing"
            className="flex-1 px-3 py-2 text-sm outline-none"
          />
        </div>
      </div>

      <GHLTagsInput tags={ghlTags} setTags={setGhlTags} />

      <div className="mt-4">
        <label htmlFor="preset-select" className="block text-sm font-medium mb-1">
          Preset:
        </label>
        <select
          id="preset-select"
          value={selectedPreset}
          onChange={(e) => setSelectedPreset(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-[#EE3333] focus:ring-1 focus:ring-[#EE3333] outline-none"
        >
          {Object.keys(PRESETS).map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {/* Wildcards */}
      <div className="mt-4">
        <label className="block text-sm font-medium mb-1">Wildcards:</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {wildcardKeys.map((key) => (
            <div key={key}>
              <label
                htmlFor={`wildcard-${key}`}
                className="block text-xs font-medium text-gray-500 mb-1"
              >
                {'{'}
                {key}
                {'}'}
              </label>
              <input
                id={`wildcard-${key}`}
                type="text"
                value={wildcards[key] || ''}
                onChange={(e) => setWildcards((prev) => ({ ...prev, [key]: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-[#EE3333] focus:ring-1 focus:ring-[#EE3333] outline-none"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Final URL */}
      <div className="mt-4">
        <div className="flex justify-between items-center mb-1">
          <h2 className="text-sm font-medium text-gray-700">Generated URL:</h2>

          {/* Botão com #EE3333 */}
          <button
            onClick={handleCopy}
            disabled={!generatedUrl}
            className="text-sm font-semibold bg-[#EE3333] cursor-pointer text-white px-3 py-1 rounded hover:bg-[#d12e2e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#EE3333]"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <textarea
          aria-label="Generated URL"
          className="w-full h-24 border border-gray-300 rounded px-3 py-2 resize-none text-sm focus:border-[#EE3333] focus:ring-1 focus:ring-[#EE3333] outline-none"
          value={generatedUrl}
          readOnly
        />
      </div>
    </>
  );
}
